"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  DEFAULT_ROWS,
  type Highlight,
  type HighlightRule,
  highlightStats,
  isHighlighted,
  MAX_ROWS,
  pascalRows,
  rowSum,
  triangleText,
  widestEntry,
} from "@/lib/tool-kit/number-theory/pascal";

/** English strings. A translated content file overrides these through `ui`. */
export const PASCALS_TRIANGLE_GENERATOR_STRINGS = {
  rows: "Number of rows",
  rowsHint:
    "1 to {max}. Row 0 is the single 1 at the top. Wide triangles scroll sideways on screen; turn off the numbers to see the pattern.",
  highlight: "Highlight",
  none: "Nothing",
  odd: "Odd numbers",
  multiples: "Multiples of m",
  diagonal: "A diagonal",
  shallow: "A shallow diagonal",
  multiplesInput: "Highlight multiples of",
  diagonalInput: "Diagonal number (0 = the ones)",
  shallowInput: "Shallow diagonal, r + c =",
  showNumbers: "Show the numbers",
  showSums: "Show row sums",
  print: "Print",
  copy: "Copy triangle",
  copied: "Copied!",
  results: "Pascal's triangle",
  triangleLabel: "Pascal's triangle with {rows} rows",
  patternLabel:
    "Pattern of Pascal's triangle with {rows} rows, highlighted entries filled in",
  rowLabel: "Row {n}",
  rowSum: "= 2{n}",
  factLastRow:
    "Row {n} has {entries} entries that add up to 2{sup} = {sum}. Its middle entry is {middle}.",
  factOdd:
    "{count} of the {total} entries are odd — the Sierpiński triangle appears in the pattern.",
  factMultiples: "{count} of the {total} entries are multiples of {m}.",
  factDiagonal0: "Diagonal 0 is all ones.",
  factDiagonal1: "Diagonal 1 holds the counting numbers 1, 2, 3, …",
  factDiagonal2: "Diagonal 2 holds the triangular numbers 1, 3, 6, 10, …",
  factDiagonal3: "Diagonal 3 holds the tetrahedral numbers 1, 4, 10, 20, …",
  factDiagonalN: "Diagonal {d} holds the binomial coefficients C(n, {d}).",
  factShallow:
    "The {count} highlighted entries add up to {sum}, which is F({index}), the Fibonacci number at that position.",
  factShallowPartial:
    "Only {count} of the entries on this shallow diagonal fit in {rows} rows; show at least {need} rows to see them all.",
  badRows: "Choose a whole number of rows from 1 to {max}.",
  badParameter: "Enter a whole number for the highlight setting: {range}.",
  rangeMultiples: "2 to 99",
  rangeDiagonal: "0 to 99",
  rangeShallow: "0 to 198",
} as const;

type Strings = typeof PASCALS_TRIANGLE_GENERATOR_STRINGS;

const DEFAULT_PARAMETERS: Record<Highlight, string> = {
  none: "0",
  odd: "0",
  multiples: "3",
  diagonal: "2",
  shallow: "9",
};
const PARAMETER_LIMITS: Record<Highlight, [number, number]> = {
  none: [0, 0],
  odd: [0, 0],
  multiples: [2, 99],
  diagonal: [0, 99],
  shallow: [0, 198],
};
const PRINT_EXACT = {
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
} as const;
const CELL = 8;
const PROSE_SM = "text-muted-foreground text-sm";

function parameterRange(mode: Highlight, strings: Strings): string {
  const ranges: Record<Highlight, string> = {
    none: "",
    odd: "",
    multiples: strings.rangeMultiples,
    diagonal: strings.rangeDiagonal,
    shallow: strings.rangeShallow,
  };
  return ranges[mode];
}

interface Cell {
  id: string;
  value: bigint;
  row: number;
  col: number;
}

/** Every entry with a stable id, so React keys never come from an array index. */
function cellsOf(
  rows: readonly bigint[][]
): { id: string; n: number; cells: Cell[] }[] {
  return rows.map((row, n) => ({
    id: `row-${n}`,
    n,
    cells: row.map((value, col) => ({
      id: `cell-${n}-${col}`,
      value,
      row: n,
      col,
    })),
  }));
}

function NumberTriangle({
  rows,
  rule,
  showSums,
  strings,
}: {
  rows: bigint[][];
  rule: HighlightRule;
  showSums: boolean;
  strings: Strings;
}) {
  const width = widestEntry(rows) + 1;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card p-3 print:overflow-visible print:border-0 print:p-0">
      <ol className="mx-auto w-max list-none space-y-1 p-0 font-mono text-xs sm:text-sm print:text-[8pt]">
        {cellsOf(rows).map((line) => (
          <li
            aria-label={t(strings.rowLabel, { n: String(line.n) })}
            className="flex items-center justify-center gap-1"
            key={line.id}
          >
            {line.cells.map((cell) => (
              <span
                className={
                  isHighlighted(cell.value, cell.row, cell.col, rule)
                    ? "rounded bg-primary/25 px-1 py-0.5 text-center font-semibold text-foreground"
                    : "px-1 py-0.5 text-center text-muted-foreground print:text-foreground"
                }
                key={cell.id}
                style={{ minWidth: `${width}ch`, ...PRINT_EXACT }}
              >
                {cell.value.toString()}
              </span>
            ))}
            {showSums ? (
              <span className="pl-3 text-muted-foreground">
                {t(strings.rowSum, { n: toSuperscript(line.n) })}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function PatternTriangle({
  rows,
  rule,
  strings,
}: {
  rows: bigint[][];
  rule: HighlightRule;
  strings: Strings;
}) {
  const count = rows.length;
  const plain = rule.mode === "none";
  return (
    <div className="rounded-lg border border-border bg-card p-3 print:border-0">
      <svg
        aria-label={t(strings.patternLabel, { rows: String(count) })}
        className="mx-auto h-auto w-full max-w-3xl"
        role="img"
        viewBox={`0 0 ${count * CELL} ${count * CELL}`}
      >
        {cellsOf(rows).flatMap((line) =>
          line.cells.map((cell) => (
            <rect
              className={
                plain || isHighlighted(cell.value, cell.row, cell.col, rule)
                  ? "fill-primary"
                  : "fill-border"
              }
              height={CELL - 1}
              key={cell.id}
              style={PRINT_EXACT}
              width={CELL - 1}
              x={((count - cell.row - 1) * CELL) / 2 + cell.col * CELL}
              y={cell.row * CELL}
            />
          ))
        )}
      </svg>
    </div>
  );
}

function Facts({
  rows,
  rule,
  strings,
}: {
  rows: bigint[][];
  rule: HighlightRule;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const lastIndex = rows.length - 1;
  const last = rows[lastIndex];
  const stats = highlightStats(rows, rule);
  const common = {
    count: fmt(stats.highlighted),
    total: fmt(stats.total),
    m: String(rule.parameter),
    d: String(rule.parameter),
  };
  const lines: string[] = [
    t(strings.factLastRow, {
      n: String(lastIndex),
      sup: toSuperscript(lastIndex),
      entries: fmt(last.length),
      sum: String(rowSum(last)),
      middle: String(last[Math.floor(lastIndex / 2)]),
    }),
  ];
  if (rule.mode === "odd") {
    lines.push(t(strings.factOdd, common));
  }
  if (rule.mode === "multiples") {
    lines.push(t(strings.factMultiples, common));
  }
  if (rule.mode === "diagonal") {
    const named = [
      strings.factDiagonal0,
      strings.factDiagonal1,
      strings.factDiagonal2,
      strings.factDiagonal3,
    ];
    lines.push(t(named[rule.parameter] ?? strings.factDiagonalN, common));
  }
  if (rule.mode === "shallow") {
    const expected = Math.floor(rule.parameter / 2) + 1;
    lines.push(
      stats.highlighted === expected
        ? t(strings.factShallow, {
            count: common.count,
            sum: String(stats.sum),
            index: String(rule.parameter + 1),
          })
        : t(strings.factShallowPartial, {
            count: common.count,
            rows: fmt(rows.length),
            need: fmt(rule.parameter + 1),
          })
    );
  }
  return (
    <div className="space-y-1 print:hidden">
      {lines.map((line) => (
        <p className={PROSE_SM} key={line}>
          {line}
        </p>
      ))}
    </div>
  );
}

export default function PascalsTriangleGeneratorWidget() {
  const strings = useToolUi(PASCALS_TRIANGLE_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [rawRows, setRawRows] = useState(String(DEFAULT_ROWS));
  const [mode, setMode] = useState<Highlight>("odd");
  const [parameters, setParameters] = useState(DEFAULT_PARAMETERS);
  const [showNumbers, setShowNumbers] = useState(true);
  const [showSums, setShowSums] = useState(false);
  const count = parseIntegerInput(rawRows);
  const rowsOk = count !== null && count >= 1 && count <= MAX_ROWS;
  const parameter = parseIntegerInput(parameters[mode]);
  const [low, high] = PARAMETER_LIMITS[mode];
  const parameterOk =
    mode === "none" ||
    mode === "odd" ||
    (parameter !== null && parameter >= low && parameter <= high);
  const rule: HighlightRule = {
    mode: parameterOk ? mode : "none",
    parameter: parameter ?? 0,
  };
  const rows = rowsOk ? pascalRows(count) : null;
  const hasParameter =
    mode === "multiples" || mode === "diagonal" || mode === "shallow";
  const parameterLabels: Record<Highlight, string> = {
    none: "",
    odd: "",
    multiples: strings.multiplesInput,
    diagonal: strings.diagonalInput,
    shallow: strings.shallowInput,
  };
  return (
    <div className="space-y-5">
      <style>{"@media print { header, footer, nav { display: none } }"}</style>
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={t(strings.rowsHint, { max: fmt(MAX_ROWS) })}
            id="ptg-rows"
            label={strings.rows}
            mono
            numeric
            onChange={setRawRows}
            value={rawRows}
          />
          {hasParameter ? (
            <TextField
              id="ptg-parameter"
              label={parameterLabels[mode]}
              mono
              numeric
              onChange={(value) =>
                setParameters((current) => ({ ...current, [mode]: value }))
              }
              value={parameters[mode]}
            />
          ) : null}
        </div>
        <PillGroup
          label={strings.highlight}
          name="ptg-highlight"
          onChange={setMode}
          options={[
            { value: "none", label: strings.none },
            { value: "odd", label: strings.odd },
            { value: "multiples", label: strings.multiples },
            { value: "diagonal", label: strings.diagonal },
            { value: "shallow", label: strings.shallow },
          ]}
          value={mode}
        />
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <Checkbox
            checked={showNumbers}
            label={strings.showNumbers}
            onChange={setShowNumbers}
          />
          <Checkbox
            checked={showSums}
            label={strings.showSums}
            onChange={setShowSums}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            disabled={rows === null}
            label={strings.copy}
            small
            text={rows ? triangleText(rows, false) : ""}
          />
        </div>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={`${WIDGET_H2} print:hidden`}>{strings.results}</h2>
        {rows === null ? (
          <ErrorNotice>{t(strings.badRows, { max: MAX_ROWS })}</ErrorNotice>
        ) : null}
        {rows !== null && !parameterOk ? (
          <ErrorNotice>
            {t(strings.badParameter, { range: parameterRange(mode, strings) })}
          </ErrorNotice>
        ) : null}
        {rows !== null && showNumbers ? (
          <section
            aria-label={t(strings.triangleLabel, { rows: String(rows.length) })}
          >
            <NumberTriangle
              rows={rows}
              rule={rule}
              showSums={showSums}
              strings={strings}
            />
          </section>
        ) : null}
        {rows !== null && !showNumbers ? (
          <PatternTriangle rows={rows} rule={rule} strings={strings} />
        ) : null}
        {rows === null ? null : (
          <Facts rows={rows} rule={rule} strings={strings} />
        )}
      </section>
    </div>
  );
}
